import { ReactTFC } from "../../../utils/fp/missing-ts-parts";
import { GuidedSellingStepChoice, NotFinalGuidedSellingStep } from "@swo/storefront";
import { css } from "@emotion/react";
import { pipe } from "fp-ts/pipeable";
import { array, nonEmptyArray, option } from "fp-ts";
import React, { useEffect } from "react";
import { ButtonsListIconChoice } from "./layouts/ButtonsListIconChoice";
import { DiagramIconSvg, svgStyles } from "./layouts/DiagramIconSvg";
import { VerticalLineChoice } from "./layouts/VerticalLineChoice";
import { SwitcherChoice } from "./layouts/SwitcherChoice";
import { VisualGridChoice } from "./VisualGridChoice";
import { GuidedSellingStepIconChoice } from "@swo/storefront";
import { NonEmptyArray } from "fp-ts/es6/NonEmptyArray";
import { ReactSVG } from "react-svg";
import cn from "classnames";
import { CSS_MQ } from "../../../styles/breakpoints";
import { fontSize } from "../../../styles/fonts";

export const GuidedSellingStepChoices: ReactTFC<{
  currentStep: NotFinalGuidedSellingStep;
  onMakeChoice: (choiceId: string) => void;
  currentChoice: string | undefined;
  onChoiceHovered: (choice: GuidedSellingStepIconChoice) => void;
}> = props => {
  const gridStyles = css`
    display: grid;
    width: 100%;
    height: 100%;

    align-self: center;
  `;

  const checkedIdx = props.currentStep.choices.findIndex(c => c.id === props.currentChoice);

  switch (props.currentStep.visualLayout) {
    case "Buttons List": {
      const elements = pipe(
        props.currentStep.choices,
        nonEmptyArray.map(choice => {
          const isChecked = choice.id === props.currentChoice;

          return (
            <ButtonsListIconChoice
              key={choice.id}
              onMakeChoice={() => props.onMakeChoice(choice.id)}
              icon={choice.icon}
              label={choice.label}
              isChecked={isChecked}
              value={choice.id}
              variant={"button-focused"}
              onChoiceHovered={() => props.onChoiceHovered(choice)}
            />
          );
        }),
      );

      return <>{elements}</>;
    }

    case "Visual Grid": {
      return (
        <VisualGridChoice
          currentChoice={props.currentChoice}
          onMakeChoice={props.onMakeChoice}
          choices={props.currentStep.choices}
          onChoiceHovered={props.onChoiceHovered}
        />
      );
    }

    case "Rising Curve": {
      function onClickIcon(idx: number) {
        const e = [...props.currentStep.choices].find((c, i) => i === idx);

        if (e) {
          props.onMakeChoice(e.id);
        }
      }

      return props.currentStep.choices.length === 3 ? (
        <div css={gridStyles}>
          <DiagramIconSvg
            onClickIcon={onClickIcon}
            elements={props.currentStep.choices.map(v => v.label)}
            className={`checked-${checkedIdx + 1}`}
            activeIdx={checkedIdx}
            css={svgStyles}
            onChoiceHovered={(idx: number) => {
              const c = props.currentStep.choices[idx];

              if (c) {
                props.onChoiceHovered(c);
              }
            }}
          />
        </div>
      ) : (
        <VerticalLineChoice
          currentStep={props.currentStep}
          onMakeChoice={props.onMakeChoice}
          currentChoice={props.currentChoice}
          checkedIdx={checkedIdx}
          onChoiceHovered={props.onChoiceHovered}
          totalChoicesCount={props.currentStep.choices.length}
        />
      );
    }

    case "Vertical Line": {
      return (
        <VerticalLineChoice
          currentStep={props.currentStep}
          onMakeChoice={props.onMakeChoice}
          currentChoice={props.currentChoice}
          checkedIdx={checkedIdx}
          onChoiceHovered={props.onChoiceHovered}
          totalChoicesCount={props.currentStep.choices.length}
        />
      );
    }

    case "Switcher": {
      return (
        <SwitcherChoice
          currentStep={props.currentStep}
          currentChoice={props.currentChoice}
          onMakeChoice={props.onMakeChoice}
          onChoiceHovered={props.onChoiceHovered}
        />
      );
    }

    case "Image Choice": {
      return (
        <ImageChoice
          currentChoice={props.currentChoice}
          onMakeChoice={props.onMakeChoice}
          choices={props.currentStep.choices}
          onChoiceHovered={props.onChoiceHovered}
        />
      );
    }

    default: {
      return null;
    }
  }
};

const ImageChoice: ReactTFC<{
  choices: NonEmptyArray<GuidedSellingStepChoice>;
  currentChoice: string | undefined;
  onMakeChoice: (choiceId: string) => void;
  onChoiceHovered: (choice: GuidedSellingStepIconChoice) => void;
}> = props => {
  const highlightedChoice = pipe(
    option.fromNullable(props.currentChoice),
    option.chain(selectedId =>
      pipe(
        props.choices,
        array.findFirst(choice => choice.id === selectedId),
      ),
    ),
    option.getOrElseW(() => nonEmptyArray.head(props.choices)),
  );

  useEffect(() => {
    if (typeof props.currentChoice === "undefined") {
      props.onMakeChoice(highlightedChoice.id);
    }
  }, [props.currentChoice]);

  const absoluteBox = css`
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
  `;
  return (
    <div
      css={css`
        display: grid;
        grid-template-rows: 3fr 1fr;

        --text-color: black;
        --text-color-disabled: #999;

        padding-bottom: 30px;

        overflow: hidden;
      `}
    >
      <div
        css={css`
          display: flex;
          justify-content: center;
          text-align: center;

          position: relative;
        `}
      >
        <div
          css={[
            absoluteBox,
            css`
              display: flex;
              flex-direction: column;
              justify-content: center;
            `,
          ]}
        >
          <ShowImageChoice choice={highlightedChoice} onMakeChoice={undefined} size={"bigger"} isDisabled={false} />
        </div>
      </div>

      <div
        css={css`
          position: relative;
        `}
      >
        <div
          css={[
            absoluteBox,
            css`
              display: flex;
              justify-content: space-between;
            `,
          ]}
        >
          {props.choices.map(choice => (
            <div
              key={choice.id}
              css={css`
                display: flex;
                text-align: center;
                justify-content: center;
              `}
            >
              <ShowImageChoice
                choice={choice}
                onMakeChoice={props.onMakeChoice}
                size={"normal"}
                isDisabled={choice.id === props.currentChoice}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const ShowImageChoice: ReactTFC<{
  choice: GuidedSellingStepChoice;
  onMakeChoice: ((choiceId: string) => void) | undefined;
  size: "normal" | "bigger";
  isDisabled: boolean;
}> = props => {
  const { onMakeChoice } = props;
  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        align-items: center;

        cursor: pointer;

        background: rgb(255 255 255 / 30%);
        border-radius: 8px;
        backdrop-filter: blur(4px) brightness(1.02);

        --t: var(--text-color);

        padding: 0 2px;
        margin: 0 2px;
        ${CSS_MQ.atLeastSm} {
          padding: 0 10px;
          margin: 0 10px;
        }
        justify-content: center;

        &.disabled {
          --t: var(--text-color-disabled);

          opacity: 0.5;
        }
      `}
      onClick={onMakeChoice ? () => onMakeChoice(props.choice.id) : undefined}
      className={cn(props.size === "normal" ? "normal" : "bigger", { disabled: props.isDisabled })}
    >
      <div
        css={css`
          .normal & {
            height: 30px;

            ${CSS_MQ.atLeastSm} {
              height: 40px;
            }

            ${CSS_MQ.atLeastMd} {
              height: 50px;
            }
          }

          .bigger & {
            height: 90px;

            ${CSS_MQ.atLeastSm} and (min-height: 850px) { {
              height: 120px;
            }

            ${CSS_MQ.atLeastMd} and (min-height: 950px) {
              height: 175px;
            }
          }

          width: 100%;
        `}
      >
        {props.choice.icon && (
          <ReactSVG
            src={props.choice.icon.url}
            title={props.choice.icon.title}
            alt={props.choice.icon.title}
            css={css`
              height: 100%;
              width: 100%;

              justify-self: center;

              svg {
                width: 100%;
                height: 100%;
                object-fit: contain;

                color: var(--t);
              }
            `}
          />
        )}
      </div>

      <button
        onClick={onMakeChoice ? () => onMakeChoice(props.choice.id) : undefined}
        className={"_label text-aa"}
        css={css`
          color: var(--t);

          padding: 4px;
          line-height: 1;

          font-weight: 300;

          .normal & {
            font-size: 14px;
          }

          .bigger & {
            ${fontSize.x2};
          }
        `}
      >
        {props.choice.label}
      </button>
    </div>
  );
};
